import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchJobs, submitJob, cancelJob, setFilters } from '../store/slices/jobsSlice';
import { generateTraceId, logUserAction } from '../utils/trace';
import JobStatusBadge from '../components/jobs/JobStatusBadge';
import JobSubmitForm from '../components/jobs/JobSubmitForm';

function JobsPage() {
  const dispatch = useDispatch();
  const { jobs, isLoading, filters } = useSelector((state) => state.jobs);
  const { activeTenant } = useSelector((state) => state.auth);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Refetch jobs when filters or active tenant changes
  useEffect(() => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'page_load', { page: 'jobs', tenantId: activeTenant?.tenant_id });
    dispatch(fetchJobs({ filters, traceId }));
  }, [dispatch, filters, activeTenant?.tenant_id]);

  const handleFilterChange = (status) => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'filter_change', { status });
    dispatch(setFilters({ status }));
  };

  const handleSubmit = async (jobData) => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'submit_job', { jobType: jobData.job_type?.name });
    await dispatch(submitJob({ ...jobData, traceId }));
    setShowSubmitModal(false);
  };

  const handleCancel = async (jobId) => {
    if (window.confirm('Are you sure you want to cancel this job?')) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'cancel_job', { jobId });
      await dispatch(cancelJob({ jobId, traceId }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900" data-testid="jobs-title">Jobs</h1>
        <button
          onClick={() => setShowSubmitModal(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition font-medium"
          data-testid="jobs-submit-button"
        >
          Submit Job
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2">
        {['all', 'pending', 'running', 'completed', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => handleFilterChange(status)}
            className={`px-4 py-2 rounded-lg capitalize transition ${
              filters.status === status
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
            data-testid={`jobs-filter-${status}`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-600" data-testid="jobs-loading">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-gray-600" data-testid="jobs-empty">
            No jobs found. Submit a job to get started.
          </div>
        ) : (
          <table className="w-full" data-testid="jobs-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <tr key={job.job_id} className="hover:bg-gray-50" data-testid={`job-row-${job.job_id}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900" data-testid={`job-id-${job.job_id}`}>
                    {job.job_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-testid={`job-type-${job.job_id}`}>
                    {job.job_type?.name || 'unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-testid={`job-status-${job.job_id}`}>
                    <JobStatusBadge status={job.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-testid={`job-created-${job.job_id}`}>
                    {formatDate(job.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {(job.status === 'running' || job.status === 'pending') && (
                      <button
                        onClick={() => handleCancel(job.job_id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                        data-testid={`job-cancel-${job.job_id}`}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <JobSubmitForm
          onSubmit={handleSubmit}
          onClose={() => setShowSubmitModal(false)}
        />
      )}
    </div>
  );
}

export default JobsPage;
